import { Injectable } from '@angular/core';
import { StorageService } from '../../services/storage/storage.service';
import { ApiService } from '../../services/apiservice/apiservice.service';
import { Observable } from 'rxjs';
import { ConfigService } from '../../services/config/config.service';
import { HttpClient } from '@angular/common/http';
import { catchError, map } from 'rxjs/operators';
import { NetworkIllPickupLocationMap } from '../request/request.service';

export interface ItemsFromOtherLibraries {
  copyRightNotice: string;
  sortByOptions: any[];
  sortedBy: string;
  items: Item;
  patronIllLimit: PatronIllLimit;
  networkIllPickupLocations?: NetworkIllPickupLocationMap;
}

export interface PatronIllLimit {
  activeRequests: number;
  limit: number;
  limitType: string;
}

export interface Item {
  active: LoanItem[];
  complete: LoanItem[];
}

export interface LoanItem {
  canDoIllCancel: boolean;
  canDoIllRenew: boolean;
  title: string;
  author: string;
  pubYear: string;
  requestId: string;
  requestDate: string;
  status: string;
  needByDate: string;
  canChangeNeedByDate: boolean;
  canChangePickupLocation: boolean;
  pickupLocations: string[];
  showPickupLocations: boolean;
  currentPickupLocation: string;
  currentStatusCode: string;
  showCancelAction: boolean;
  showRenewAction: boolean;
  documentURL: string;
  requestFlag: string;
  minNeedByDate: string;
  libraryDBPoolKey: number;
  agControlId: number;
  databaseType: string;
  relativeUrl: string;
  documentInfo: any[];
  dueDate: string;
  patronMessage: string;
  requestType: string;
  illPickupLocation?: string;
}

//TOOD Defined else where
export interface NameValueChk {
  name: string;
  value: string;
  isChecked: boolean;
}

export interface ItemsOnHoldModel {
  hasLocal: boolean;
  hasUnsuspend: boolean;
  hasSuspend: boolean;
  sortByOptions: NameValueChk[];
  sortedBy: string;
  filterOptions: NameValueChk[];
  filteredBy: string;
  items: ItemsOnHoldCommon[];
}

export interface JacketArt {
  source: string;
  type: string;
  small: string;
  medium: string;
  large: string;
  link: string;
}

export interface FieldShowValue {
  show: boolean;
  value: string;
}

export interface ItemsOnHoldDisplay {
  title: FieldShowValue;
  author: FieldShowValue;
  isbn: FieldShowValue;
  materialType: FieldShowValue;
  pubYear: FieldShowValue;
  volume: FieldShowValue;
  reserveDateStr: FieldShowValue;
  expirationDate: FieldShowValue;
  reserveSequence: FieldShowValue;
  pickupLocation: FieldShowValue;
  suspendedUntilDate: FieldShowValue;
  status: FieldShowValue;
}

export interface ItemsOnHoldCommon {
  displayFields: ItemsOnHoldDisplay;
  reserveDate: Date;
  jacketArt: JacketArt;
  agItem: ReservedItem;
  odItem: OverDriveReservedItem;
  bxItem: Axis360OnHoldItem;
  hpItem: HooplaReservedItem;
  resourceType: string;
  canSuspend: boolean;
  canRemoveHold: boolean;
  canCheckout: boolean;
  relativeUrl: string;

  //ui only
  selected: boolean;
}

export interface IReservedItem {
  title: string;
  author: string;
  isbn: string;
  materialType: string;
  publicationYear: string;
  holdsQueuePosition: number;
  holdPlacedDate: Date;
}

export interface ISuspendableReservedItem extends IReservedItem {
  isHoldSuspended: boolean;
  suspendTillDate: Date;
}

export interface ReservedItem extends ISuspendableReservedItem {
  reserveId: number;
  agControlId: number;
  holdingsId: number;
  expireDate: Date;
  expiredReason: string;
  patronNote: string;
  staffNote: string;
  pickupLocationId: number;
  pickupLocationName: string;
  volume: string;
  isInTransit: boolean;
  inTransitMessage: string;
  softHoldingsID: number;
  jacketArt: JacketArt;
  libraryProfileKey: number;
  materialType: string;
  allowPatronToSeeStaffNote: boolean;
  canModifyStaffNote: boolean;
  canModifyPatronNote: boolean;
}

export interface OverDriveReservedItem extends ISuspendableReservedItem {
  productId: string;
  emailAddress: string;
  materialType: string;
  isReadyForPickup: boolean;
  thumbnailImage: string;
  coverImage: string;
  numberOfSuspensionDays: number;
  suspendedUntil: string;
  //formatDetails: FormatDetail[];
  isSuspendIndefinite: boolean;
}

export interface Axis360OnHoldItem extends IReservedItem {
  materialType: string;
  reservedUntilDate: string;
  jacketArtUrl: string;
  canCheckout: boolean;
  canRemoveHold: boolean;
  titleId: string;
  format: string;
  loanPeriod: number;
}

//TODO Defined elsewhere
export interface IHooplaItem {
  id: number;
  availability: string;
  availableCopies: number;
  totalCopies: number;
  holdsInQueue: number;
  isCheckedOut: boolean;
  isReserved: boolean;
  isReadyForPickup: boolean;
  downloadLink: string;
  hasAccount: boolean;
  status: HOOPLA_ITEM_STATUS;
}

export enum HOOPLA_ITEM_STATUS {
  BORROW = 'BORROW',
  HOLD = 'HOLD',
  UNAVAILABLE = 'UNAVAILABLE',
}

export interface HooplaReservedItem extends IHooplaItem, IReservedItem {
  format: string;
  suspendTillDate: Date;
}

@Injectable()
export class ItemsDetailsService {
  url = '';

  // items on hold variables
  filterBy = 'all';
  sortBy = 'title';

  //item from other libs variables
  sortByforLibs = 'title';
  private readonly _chKey: string = '_ch';

  constructor(
    private _apiService: ApiService,
    private _storageService: StorageService,
    private _configService: ConfigService,
    private _http: HttpClient,
  ) {}

  getCheckoutHistCached(): any[] {
    return this._storageService.getItem(this._chKey);
  }
  setCheckoutHistCached(info): void {
    this._storageService.setItem(this._chKey, info);
  }

  //   ---------------------------------------------------------------------------
  //   ------------------------------ LOST Items---------------------------------
  //   -------------------------------------------------------------------------

  // get lost items from api
  lostItems(): Observable<any> {
    this.url = this._configService.agApi + '/account/GetLostItemsByPatron';
    return this._apiService.get(this.url);
  }

  //   ---------------------------------------------------------------------------
  //   ------------------------------ CHECKOUT HISTORY--------------------------
  //   -------------------------------------------------------------------------

  checkoutHistory(year): Observable<any> {
    const data = year;
    this.url = this._configService.agApi + '/account/GetCheckoutHistory' + '?year=' + data;
    return this._apiService.get(this.url).pipe(
      map((res) => {
        return res ?? null;
      }),
      catchError((error: unknown) => {
        return null;
      }),
    );
  }

  turnOnCheckoutHistory(): Observable<any> {
    this.url = this._configService.agApi + '/account/TurnOnCheckoutHistory';
    return this._apiService.get(this.url);
  }

  turnOffCheckoutHistory(): Observable<any> {
    this.url = this._configService.agApi + '/account/TurnOffCheckoutHistory';
    return this._apiService.get(this.url);
  }

  downloadExcelFile(data): Observable<any> {
    this.url = this._configService.agApi + '/account/DownloadCheckoutHistory' + '?year=' + data;
    return this._apiService.get(this.url);
  }

  //   ---------------------------------------------------------------------------
  //   ------------------------------ITEMS ON HOLD-------------------------------------
  //   ---------------------------------------------------------------------------

  // get reserved items under patron
  getReservedItemsUnderPatron(): Observable<ItemsOnHoldModel> {
    const filterBy = this._storageService.getItem('filterItemsHold') ?? 'all';
    const sortBy = this._storageService.getItem('sortItemsHold') ?? 'title';
    this.url =
      this._configService.agApi +
      '/account/GetAllReservesUnderPatron' +
      '?filterBy=' +
      filterBy +
      '&sortBy=' +
      sortBy;
    return this._apiService.get(this.url).pipe(map((r) => r.data));
  }

  suspendItems(data): Observable<any> {
    this.url = this._configService.agApi + '/account/SuspendReserves';
    return this._apiService.post(this.url, data);
  }

  unSuspendItems(data): Observable<any> {
    this.url = this._configService.agApi + '/account/UnsuspendReserves';
    return this._apiService.post(this.url, data);
  }

  cancelItems(data): Observable<any> {
    this.url = this._configService.agApi + '/account/CancelSelectedReserveItems';
    return this._apiService.post(this.url, data);
  }

  updatePatronNotes(data): Observable<any> {
    this.url = this._configService.agApi + '/account/UpdatePatronNotes';
    return this._apiService.post(this.url, data);
  }

  placeHoldItemsOD(data): Observable<any> {
    this.url = this._configService.agApi + '/econtent/PlaceHoldSuspension';
    return this._apiService.post(this.url, data);
  }

  cancelHoldOD(data): Observable<any> {
    this.url = this._configService.agApi + '/econtent/RemoveHold';
    return this._apiService.post(this.url, data);
  }

  //   ---------------------------------------------------------------------------
  //   ------------------------------ ITEMS OUT-------------------------------------
  //   ---------------------------------------------------------------------------

  getPatronIlsCheckedOutItems(): Observable<any> {
    let filterBy = this._storageService.getItem('filterItemsOut');
    let sortBy = this._storageService.getItem('sortItemsOut');
    if (!filterBy) {
      filterBy = 'all';
    }
    if (!sortBy) {
      sortBy = 'title';
    }

    this.url =
      this._configService.agApi +
      '/account/GetPatronIlsCheckedOutItems' +
      '?filterBy=' +
      filterBy +
      '&sortBy=' +
      sortBy;
    return this._apiService.get(this.url);
  }

  renewAll(): Observable<any> {
    const data = '';
    this.url = this._configService.agApi + '/account/PatronIlsSelfRenewAll';
    return this._apiService.post(this.url, data);
  }

  // verso api calls
  renewItem(data): Observable<any> {
    this.url = this._configService.agApi + '/account/PatronIlsSelfRenew';
    return this._apiService.post(this.url, data);
  }

  //   ---------------------------------------------------------------------------
  //   ------------------ITEMS FROM OTHER LIBRARIES-------------------------------
  //   ---------------------------------------------------------------------------

  getPatronIllRequests(sortBy: string): Observable<ItemsFromOtherLibraries> {
    this.url = this._configService.agApi + '/account/GetPatronIllRequests' + '?sortBy=' + sortBy;
    return this._apiService.get(this.url).pipe(map((r) => r.data));
  }

  hideItemRequest(data): Observable<any> {
    this.url = this._configService.agApi + '/account/HidePatronIllRequest';
    return this._apiService.post(this.url, data);
  }

  showPatronIllRequests(): Observable<any> {
    const data = '';
    this.url = this._configService.agApi + '/account/ShowPatronIllRequests';
    return this._apiService.post(this.url, data);
  }

  cancelPatronIllRequest(data): Observable<any> {
    this.url = this._configService.agApi + '/account/CancelPatronIllRequest';
    return this._apiService.post(this.url, data);
  }

  renewPatronIllRequest(data): Observable<any> {
    this.url = this._configService.agApi + '/account/RenewPatronIllRequest';
    return this._apiService.post(this.url, data);
  }

  async updatePatronIllRequestNeedByDateAsync(data) {
    try {
      const url = `${this._configService.agApi}/account/UpdatePatronIllRequestNeedByDate`;
      const response: any = await this._http.post(url, data).toPromise();
      if (response) {
        if (
          response.status == '200 OK' &&
          response.message.toLowerCase() == 'success' &&
          response.data !== null
        ) {
          return response.data;
        }
      }
    } catch (error) {
      console.error(
        `itemDetails.service:updatePatronIllRequestNeedByDateAsync => ${error.message}`,
      );
    }
    return null;
  }

  updatePatronIllRequestPickupLocationAsync(data): Observable<any> {
    const url = `${this._configService.agApi}/account/UpdatePatronIllRequestPickupLocation`;
    return this._apiService.post(url, data);
  }

  //   ---------------------------------------------------------------------------
  //   -------------------FINE AND LOST ITEMS-------------------------------------
  //   ---------------------------------------------------------------------------

  getFinesPayment(): Observable<any> {
    this.url = this._configService.agApi + '/account/GetFinesPayment';
    return this._apiService.get(this.url);
  }

  getPayPalUrl(amount): Observable<any> {
    this.url = this._configService.agApi + '/account/GetPayPalUrl?AmountToPay=' + amount;
    return this._apiService.get(this.url);
  }

  limitMessage(data: PatronIllLimit): string {
    const { limit, limitType, activeRequests } = data;

    // Unlimited → show nothing
    if (limit === -1) {
      return '';
    }

    // Blocked account
    if (limit === 0) {
      return '0 new requests can be made';
    }

    const available = limit - activeRequests;
    const normalizedLimitType = limitType?.trim().toLowerCase();
    const period = ['active', 'no limit', 'undefined'].includes(normalizedLimitType)
      ? ''
      : ` this ${normalizedLimitType}`;

    // At or over limit
    if (available <= 0) {
      return `0 new requests can be made${period}`;
    }

    // Has some available
    const plural = available === 1 ? 'request' : 'requests';
    return `${available} new ${plural} can be made${period}`;
  }

  requestAvailable(data: PatronIllLimit): string {
    const { limit, limitType, activeRequests } = data;

    // Unlimited → show nothing
    if (limit === -1) {
      return '';
    }

    // Blocked account
    if (limit === 0) {
      return '0 new requests can be made';
    }

    const available = limit - activeRequests;
    const normalizedLimitType = limitType?.trim().toLowerCase();
    const period = ['active', 'no limit', 'undefined'].includes(normalizedLimitType)
      ? ''
      : ` this ${normalizedLimitType}`;

    // At or over limit
    return available <= 0 ? `0 new requests can be made${period}` : '';
  }
}
